import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { saveAs } from 'file-saver'
import { toast } from 'react-toastify'
import isemail from 'isemail'

import { postRequest } from '../../lib/service/fetchService'
import LoadingSpinner from '../../components/LoadingSpinner'

import config from '../../config'

export const InfoPanel = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.nswPaletteWhite};
`
InfoPanel.displayName = 'InfoPanel'

export const InfoPanelGrey = styled(InfoPanel)`
  background-color: ${({ theme }) => theme.palette.nswPaletteOffWhite};
`
InfoPanelGrey.displayName = 'InfoPanelGrey'

const Heading = styled.span`
  font-size: 38px;
  margin-bottom: 8px;
`
Heading.displayName = 'Heading'

const ReferenceCode = styled.span`
  font-size: 46px;
  margin-bottom: 8px;
  font-weight: 800;
  color: ${({ theme }) => theme.palette.nswBrandDark};
`
ReferenceCode.displayName = 'ReferenceCode'

const ButtonContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
`
ButtonContainer.displayName = 'ButtonContainer'

const PdfButtonContent = styled.div`
  display: flex;
  align-items: center;
`
PdfButtonContent.displayName = 'PdfButtonContent'

export const ButtonSpinner = styled(LoadingSpinner)`
  display: flex;
`
ButtonSpinner.displayName = 'ButtonSpinner'

export function TrackingCodePanel({ trackingCode }: { trackingCode: string }) {
  return (
    <InfoPanel className="fa-info-panel ob-information__content ql-editor">
      <b>Your reference code is:</b>
      <ReferenceCode className="fa-info-panel__tracking-code">
        {trackingCode}
      </ReferenceCode>
    </InfoPanel>
  )
}

export function DownloadPdf({
  submissionId,
  trackingCode,
  formId,
}: {
  submissionId: string
  trackingCode: string
  formId: number
}) {
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const downloadPdf = useCallback(async () => {
    try {
      setIsDownloadingPdf(true)
      const { body } = await postRequest<{ fileName: string; data: string }>({
        url: `${config.OB_HOSTED_API_URL}/submission-pdf`,
        resource: {
          submissionId,
          formId,
          trackingCode,
        },
      })

      if (body) {
        const resp = await fetch(`data:application/pdf;base64,${body.data}`)
        saveAs(await resp.blob(), body.fileName)
      }
    } catch (e) {
      console.error(e)
      toast.error('There was an issue while trying to download your PDF', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [submissionId, trackingCode, formId])

  return (
    <ButtonContainer className="fa-download-pdf">
      <button
        className="button is-primary"
        disabled={isDownloadingPdf}
        onClick={downloadPdf}
      >
        {isDownloadingPdf ? (
          <PdfButtonContent>
            <span>Downloading PDF</span>
            <ButtonSpinner />
          </PdfButtonContent>
        ) : (
          <span>Download a PDF copy of your form</span>
        )}
      </button>
    </ButtonContainer>
  )
}

const InputWithButton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
InputWithButton.displayName = 'InputWithButton'

export function SendPdfEmail({
  submissionId,
  formId,
}: {
  submissionId: string
  formId: number
}) {
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false)
  const [emailAddress, setEmailAddress] = useState<string>('')

  const onEmailAddressChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setEmailAddress(e.currentTarget.value)
    },
    [],
  )

  const resendSubmissionEmail = useCallback(async () => {
    try {
      setIsSendingEmail(true)
      await postRequest({
        url: `${config.OB_HOSTED_API_URL}/submission-pdf/resend`,
        resource: {
          submissionId,
          emailAddress,
          formId,
        },
      })
      toast.success('Email successfully sent', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } catch (e) {
      console.log('e: ', e)
      toast.error('There was an issue while trying to send your PDF email', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } finally {
      setIsSendingEmail(false)
    }
  }, [emailAddress, submissionId, formId])

  return (
    <>
      <label className="fa-email-pdf__label">
        Send a copy to another recipient (e.g. transport driver, livestock
        agent)
      </label>
      <InputWithButton className="fa-email-pdf__input">
        <input
          type="email"
          className="input ob-input"
          onChange={onEmailAddressChange}
        ></input>

        <button
          className="button is-primary ob-lookup__button"
          onClick={resendSubmissionEmail}
          disabled={!isemail.validate(emailAddress) || isSendingEmail}
        >
          {isSendingEmail ? <ButtonSpinner /> : <span>Email</span>}
        </button>
      </InputWithButton>
    </>
  )
}
